import React from 'react';
import {alpha, Box, Paper, Stack, Typography} from '@mui/material';
import {
    Article,
    AutoAwesome,
    ChatBubbleOutline,
    Link as LinkIcon,
    PersonOutline,
    PictureAsPdf,
    Verified,
} from '@mui/icons-material';

const sources = [
    {
        label: '[1] Ingredient list',
        detail: 'Milk 82.6%, whole milk powder 7.0%',
        icon: <Article sx={{fontSize: 14}}/>,
    },
    {
        label: '[2] Allergen mapping',
        detail: 'Milk ingredients mapped to milk allergen',
        icon: <LinkIcon sx={{fontSize: 14}}/>,
    },
    {
        label: '[3] EU labelling rule',
        detail: 'Allergen declaration reference',
        icon: <PictureAsPdf sx={{fontSize: 14}}/>,
    },
];

export default function SourcesTransparencyVisual() {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 270,
                p: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 2,
                        py: 1.45,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{fontWeight: 800}}>
                            Traceable assistant answer
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Sources visible inside the response
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            px: 1.2,
                            py: 0.5,
                            borderRadius: 10,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{fontWeight: 800, color: 'primary.main'}}
                        >
                            Sources linked
                        </Typography>
                    </Box>
                </Stack>

                {/* Chat */}
                <Box sx={{p: 2}}>
                    <Stack spacing={1.5}>
                        {/* User message */}
                        <Stack direction="row" spacing={1.2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.4,
                                }}
                            >
                                <PersonOutline sx={{fontSize: 16}}/>
                            </Box>

                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1.2,
                                    borderRadius: 2,
                                    maxWidth: '88%',
                                    boxShadow: 'none',
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <ChatBubbleOutline sx={{fontSize: 16, color: 'primary.main', mt: 0.2}}/>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{display: 'block', mb: 0.4}}
                                        >
                                            User
                                        </Typography>

                                        <Typography variant="body2" sx={{fontWeight: 700, lineHeight: 1.5}}>
                                            Which allergens should be declared for the current formulation?
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>

                        {/* Assistant message */}
                        <Stack direction="row" spacing={1.2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.4,
                                }}
                            >
                                <AutoAwesome sx={{fontSize: 16}}/>
                            </Box>

                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1.3,
                                    borderRadius: 2,
                                    maxWidth: '92%',
                                    boxShadow: 'none',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{display: 'block', mb: 0.5, fontWeight: 800}}
                                >
                                    Assistant
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{lineHeight: 1.6}}
                                >
                                    The current formulation includes <strong>milk</strong> and{' '}
                                    <strong>whole milk powder</strong>, both mapped to the milk allergen category.
                                    The generated declaration should therefore include{' '}
                                    <strong>milk and products thereof</strong>.
                                </Typography>


                                {/* Sources */}
                                <Box
                                    sx={{
                                        mt: 1.4,
                                        pt: 1.2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{mb: 0.9}}>
                                        <Verified sx={{fontSize: 15, color: 'success.main'}}/>
                                        <Typography
                                            variant="caption"
                                            sx={{fontWeight: 800, color: 'text.secondary'}}
                                        >
                                            Sources used · High confidence
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={0.75}>
                                        {sources.map((source) => (
                                            <Paper
                                                key={source.label}
                                                variant="outlined"
                                                sx={{
                                                    px: 1,
                                                    py: 0.75,
                                                    borderRadius: 1.7,
                                                    bgcolor: 'background.paper',
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: '50%',
                                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                            color: 'primary.main',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {source.icon}
                                                    </Box>

                                                    <Box sx={{minWidth: 0}}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                fontWeight: 800,
                                                                fontSize: 10.8,
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {source.label}
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'block',
                                                                fontSize: 10.3,
                                                                lineHeight: 1.2,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {source.detail}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            </Paper>
                        </Stack>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}